import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import { addressBookApi, AddressBookEntry } from '@/services/api/agentApi';

interface AddressBookState {
  entries: AddressBookEntry[];
  isLoading: boolean;
  error: string | null;
}

interface AddressBookActions {
  fetchEntries: () => Promise<void>;
  upsertEntry: (data: Partial<AddressBookEntry>) => Promise<void>;
  deleteEntry: (contactId: string) => Promise<void>;
  seedFromConnections: () => Promise<void>;
}

export const useAddressBookStore = create<AddressBookState & AddressBookActions>()(
  subscribeWithSelector(
    immer((set, get) => ({
      entries: [],
      isLoading: false,
      error: null,

      fetchEntries: async () => {
        set((s) => { s.isLoading = true; s.error = null; });
        try {
          const result = await addressBookApi.list();
          set((s) => { s.entries = result.data || []; s.isLoading = false; });
        } catch (err) {
          set((s) => { s.error = (err as Error).message; s.isLoading = false; });
        }
      },

      upsertEntry: async (data) => {
        try {
          await addressBookApi.upsert(data);
          await get().fetchEntries();
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      deleteEntry: async (contactId) => {
        try {
          await addressBookApi.remove(contactId);
          set((s) => { s.entries = s.entries.filter(e => e.contact_id !== contactId); });
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      seedFromConnections: async () => {
        try {
          await addressBookApi.seed();
          await get().fetchEntries();
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },
    }))
  )
);

// Reset on logout to prevent data leakage between sessions
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useAddressBookStore.setState({ entries: [], isLoading: false, error: null });
        }
      }
    );
  });
}
